import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Smartphone, Download, CheckCircle, Heart, Calendar, Share2 } from 'lucide-react';

export const ClientWelcomePage = ({ onDismiss }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    // Detectar si ya está en modo standalone (instalada)
    const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                      window.navigator.standalone ||
                      document.referrer.includes('android-app://');
    
    setIsStandalone(standalone);

    // Detectar iOS
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    // Capturar el evento de instalación
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // Si no hay prompt (iOS o ya instalado), mostrar instrucciones
      setShowInstructions(true);
      return;
    }

    // Mostrar prompt de instalación
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('PWA instalada');
    }
    
    setDeferredPrompt(null);
  };

  // Si ya está instalada, marcar como visto y continuar
  if (isStandalone && onDismiss) {
    onDismiss();
    return null;
  }

  const handleContinue = () => {
    if (onDismiss) {
      onDismiss();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full space-y-6">
        {/* Logo y título */}
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <img 
              src="/logo.png" 
              alt="Luis Herrera Eventos" 
              className="h-24 w-auto"
            />
          </div>
          <h1 className="text-4xl font-bold text-white">
            Portal del Cliente
          </h1>
          <p className="text-xl text-emerald-300">
            Gestiona tu evento de manera fácil
          </p>
        </div>

        {/* Card principal */}
        <Card className="bg-slate-800/50 border-emerald-700 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white text-center text-2xl">
              Bienvenido a tu Evento 🎉
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-slate-300 text-center">
              Accede a todos los detalles de tu evento y gestiona tus peticiones musicales desde tu móvil.
            </p>

            {/* Características para clientes */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex flex-col items-center text-center space-y-2">
                <Heart className="w-8 h-8 text-pink-400" />
                <p className="text-slate-300 text-sm">Peticiones Musicales</p>
              </div>
              <div className="flex flex-col items-center text-center space-y-2">
                <Calendar className="w-8 h-8 text-emerald-400" />
                <p className="text-slate-300 text-sm">Detalles del Evento</p>
              </div>
              <div className="flex flex-col items-center text-center space-y-2">
                <Share2 className="w-8 h-8 text-purple-400" />
                <p className="text-slate-300 text-sm">Comparte Preferencias</p>
              </div>
            </div>

            {/* Botón de instalación */}
            {!showInstructions ? (
              <div className="space-y-3">
                <Button
                  onClick={handleInstallClick}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg"
                  size="lg"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Instalar en mi Móvil
                </Button>
                
                <Button
                  onClick={handleContinue}
                  variant="outline"
                  className="w-full border-emerald-600 text-emerald-300 hover:bg-emerald-900/20"
                >
                  Continuar sin instalar
                </Button>
              </div>
            ) : (
              // Instrucciones de instalación (especialmente para iOS)
              <Card className="bg-slate-900/50 border-emerald-600">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <Smartphone className="w-5 h-5 text-emerald-400" />
                    Instrucciones de Instalación
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {isIOS ? (
                    // Instrucciones iOS
                    <div className="space-y-3 text-slate-300">
                      <p className="font-semibold text-white">En Safari (iPhone/iPad):</p>
                      <ol className="list-decimal list-inside space-y-2 text-sm">
                        <li>Toca el botón <strong>Compartir</strong> (cuadrado con flecha ↑)</li>
                        <li>Desplázate hacia abajo</li>
                        <li>Selecciona <strong>"Añadir a pantalla de inicio"</strong></li>
                        <li>Toca <strong>"Añadir"</strong></li>
                      </ol>
                      <div className="flex items-start gap-2 p-3 bg-emerald-900/20 border border-emerald-600/30 rounded">
                        <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <p className="text-sm">
                          La app aparecerá en tu pantalla de inicio con un icono verde
                        </p>
                      </div>
                    </div>
                  ) : (
                    // Instrucciones Android/Chrome y Safari Mac
                    <div className="space-y-4 text-slate-300">
                      <div>
                        <p className="font-semibold text-white mb-2">En Chrome (Android):</p>
                        <ol className="list-decimal list-inside space-y-2 text-sm">
                          <li>Toca el menú <strong>⋮</strong> (tres puntos)</li>
                          <li>Selecciona <strong>"Instalar aplicación"</strong></li>
                          <li>Confirma la instalación</li>
                        </ol>
                      </div>
                      
                      <div className="border-t border-emerald-600/30 pt-3">
                        <p className="font-semibold text-white mb-2">En Safari (Mac):</p>
                        <ol className="list-decimal list-inside space-y-2 text-sm">
                          <li>Click en <strong>Archivo</strong> en el menú superior</li>
                          <li>Selecciona <strong>"Añadir a Dock"</strong></li>
                          <li>La app aparecerá en tu Dock de macOS</li>
                        </ol>
                      </div>
                      
                      <div className="flex items-start gap-2 p-3 bg-emerald-900/20 border border-emerald-600/30 rounded">
                        <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <p className="text-sm">
                          Tendrás acceso rápido desde tu pantalla de inicio o Dock
                        </p>
                      </div>
                    </div>
                  )}
                  
                  <Button
                    onClick={handleContinue}
                    className="w-full bg-emerald-700 hover:bg-emerald-600 text-white mt-4"
                  >
                    Entendido, continuar
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Nota sobre acceso */}
            <div className="text-center text-sm text-slate-400 space-y-1">
              <p>🔐 <strong>Necesitarás un código de acceso</strong></p>
              <p>Tu DJ te habrá proporcionado un código único para acceder</p>
            </div>

            {/* Ventajas de instalar */}
            <Card className="bg-emerald-900/20 border-emerald-600/30">
              <CardContent className="pt-4">
                <h3 className="text-white font-semibold mb-3 text-center">
                  ¿Por qué instalar?
                </h3>
                <ul className="space-y-2 text-slate-300 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span>Acceso rápido desde tu pantalla de inicio</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span>No ocupa espacio en tu móvil</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span>Funciona incluso sin conexión</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span>Actualización automática siempre</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-slate-500 text-sm">
          <p>© 2025 Luis Herrera Eventos</p>
          <p className="mt-1 text-emerald-400">Tu evento, tu música 🎵</p>
        </div>
      </div>
    </div>
  );
};
